<template>
  <div v-if="participants.length" class="border-t border-gray-200 pt-2">
    <p class="text-xs font-semibold text-gray-500 uppercase mb-1.5 px-1">
      Participants ({{ participants.length }})
    </p>
    <div class="space-y-1">
      <div
        v-for="p in participants"
        :key="p.call_sid"
        class="flex items-center justify-between px-2 py-1.5 rounded-lg"
        :class="p.status === 'on_hold' ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'"
      >
        <div class="flex items-center gap-2 min-w-0">
          <span class="inline-block w-2 h-2 rounded-full shrink-0" :class="statusDotClass(p)" />
          <span class="text-xs font-medium text-gray-500 uppercase shrink-0">{{ p.role?.display_name || displayLabel(p.label) }}</span>
          <span v-if="p.phone_book_entry" class="sensitive text-sm font-medium text-neutral-800 truncate">{{ p.phone_book_entry.name }}</span>
          <span v-if="p.phone_number" class="sensitive text-sm text-neutral-800 truncate">{{ formatPhone(p.phone_number) }}</span>
          <span v-if="isProvider(p)" class="text-xs text-gray-400">(You)</span>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <span v-if="p.status === 'connecting' || p.status === 'ringing'" class="text-xs text-gray-400 italic mr-1">
            {{ p.status === 'connecting' ? 'Dialing...' : 'Ringing...' }}
          </span>
          <span v-if="p.status === 'on_hold'" class="text-xs text-amber-600 font-medium mr-1">On Hold</span>
          <template v-if="!isProvider(p)">
            <button
              v-tooltip="holdTooltip(p)"
              class="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
              :aria-label="p.status === 'on_hold' ? 'Resume' : 'Hold'"
              :disabled="!canHold(p)"
              @click="$emit('hold', p, p.status !== 'on_hold')"
            >
              <FontAwesomeIcon :icon="['fas', p.status === 'on_hold' ? 'play' : 'pause']" :class="p.status === 'on_hold' ? 'text-green-600' : 'text-amber-600'" class="text-xs" />
            </button>
            <button
              v-tooltip="removeTooltip(p)"
              class="w-7 h-7 flex items-center justify-center rounded hover:bg-red-100 transition-colors"
              aria-label="Remove from call"
              @click="$emit('remove', p)"
            >
              <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-red-500 text-xs" />
            </button>
          </template>
        </div>
      </div>
    </div>
    <button
      v-if="showLeaveButton"
      v-tooltip="leaveTooltip"
      class="mt-2 w-full text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg px-3 py-1.5 transition-colors"
      @click="$emit('leave')"
    >
      <FontAwesomeIcon :icon="['fas', 'right-from-bracket']" class="mr-1" />
      Leave Call
    </button>
  </div>
</template>

<script>
import tooltip from '@/directives/tooltip'
import {
  LEAVE_CALL_TOOLTIP,
  canHoldParticipant,
  participantHoldTooltip,
  participantRemoveTooltip,
} from '@/utils/conferenceParticipants'

export default {
  name: 'ConferenceParticipantList',
  directives: { tooltip },
  props: {
    participants: { type: Array, default: () => [] },
    providerCallSid: { type: String, default: '' },
  },
  emits: ['hold', 'remove', 'leave'],
  data() {
    return { leaveTooltip: LEAVE_CALL_TOOLTIP }
  },
  computed: {
    showLeaveButton() {
      const nonProviderConnected = this.participants.filter(
        (p) => !this.isProvider(p) && (p.status === 'connected' || p.status === 'on_hold'),
      )
      return nonProviderConnected.length >= 2
    },
  },
  methods: {
    canHold(p) {
      return canHoldParticipant(p)
    },
    holdTooltip(p) {
      return participantHoldTooltip(p)
    },
    removeTooltip(p) {
      return participantRemoveTooltip(p)
    },
    isProvider(p) {
      return p.label === 'provider' || p.call_sid === this.providerCallSid
    },
    displayLabel(label) {
      const labels = {
        provider: 'Provider',
        patient: 'Patient',
        translator: 'Translator',
        central_scheduling: 'Scheduling',
        pcp_practice: 'PCP',
        dme_company: 'DME',
      }
      return labels[label] || label
    },
    statusDotClass(p) {
      if (p.status === 'connected') return 'bg-green-400'
      if (p.status === 'on_hold') return 'bg-amber-400'
      if (p.status === 'connecting' || p.status === 'ringing') return 'bg-blue-400 animate-pulse'
      if (p.status === 'left' || p.status === 'failed') return 'bg-gray-300'
      return 'bg-gray-300'
    },
    formatPhone(phone) {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
  },
}
</script>
